const FOLDER_MAX_DEPTH_CODE = 'ERR_FOLDER_MAX_DEPTH';
const FOLDER_MAX_DEPTH_MESSAGE =
  'Cannot create folder: maximum nesting depth of 6 levels reached.';

/**
 * Maps backend folder errors to user-friendly messages.
 *
 * Accepts either:
 *  - An ApolloError (has graphQLErrors array with extensions.code)
 *  - A plain error string / object with a message field
 */
export const mapFolderError = (error: any): string => {
  // When rejectWithServerResponse=true, the rejected value is the serverErrors
  // array directly: [{ message, extensions: { code } }]
  // Also handle ApolloError shape: { graphQLErrors: [...] }
  const firstGqlError: any =
    (Array.isArray(error) ? error[0] : null) ??
    error?.graphQLErrors?.[0] ??
    null;

  if (firstGqlError) {
    const code = firstGqlError?.extensions?.code;
    const gqlMessage: string = firstGqlError?.message ?? '';

    if (
      code === FOLDER_MAX_DEPTH_CODE ||
      gqlMessage.includes('Maximum folder nesting depth')
    ) {
      return FOLDER_MAX_DEPTH_MESSAGE;
    }

    if (
      code === 'ERR_FOLDER_UNAUTHORIZED' ||
      gqlMessage.includes('permission')
    ) {
      return 'You no longer have permission to modify folders in this room.';
    }

    if (
      code === 'ERR_FOLDER_NOT_FOUND' ||
      code === 'ERR_PARENT_FOLDER_NOT_FOUND'
    ) {
      return 'The folder could not be found. It may have been deleted.';
    }
  }

  // Fallback: plain string or object with message
  const message: string =
    error?.message ?? (typeof error === 'string' ? error : '');

  if (
    message.includes('depth') ||
    message.includes('nesting') ||
    message.includes('Maximum folder')
  ) {
    return FOLDER_MAX_DEPTH_MESSAGE;
  }

  if (message.includes('permission') || error?.status === 403) {
    return 'You no longer have permission to modify folders in this room.';
  }

  if (message.includes('timeout') || message.includes('timed out')) {
    return 'Request timed out. Please check your connection and try again.';
  }

  if (message.includes('offline') || message.includes('network')) {
    return 'Network connection lost. Please check your internet connection.';
  }

  if (message.includes('not found') || error?.status === 404) {
    return 'The folder could not be found. It may have been deleted.';
  }

  if (message.includes('duplicate') || message.includes('already exists')) {
    return 'A folder with this name already exists.';
  }

  if (message.includes('rename') || message.includes('update')) {
    return 'Failed to rename folder. Please try again.';
  }

  return message || 'Failed to complete folder operation. Please try again.';
};

export { FOLDER_MAX_DEPTH_CODE, FOLDER_MAX_DEPTH_MESSAGE };
